import { HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';

import { Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';

import {
    CustomHttpParamEncoder,
    ExportLocation,
    ExportLocationId,
    OrganisationId,
    Paginated,
    ShApiCachingHelper,
    ShPaginationService,
    User,
    UserId,
    UserService
} from '@stylehaven/components';
import { HttpService } from '../http/http.service';

@Injectable({ providedIn: 'root' })
export class ExportLocationsService {
    private readonly exportLocationsEndpoint: string = `v2/export-locations`;
    private exportLocations$: Observable<ExportLocation[]>;
    private defaultExportLocation$: Observable<ExportLocation>;

    constructor(
        private readonly http: HttpService,
        private readonly paginationService: ShPaginationService,
        private readonly userService: UserService,
        private apiCachingHelper: ShApiCachingHelper
    ) {
        this.initialize();
    }

    initialize() {
        this.exportLocations$ = this.apiCachingHelper.cachedApiCall(
            this.userService.getCurrentUser().pipe(
                map(user => user.Organisation.Id),
                switchMap(orgId =>
                    this.paginationService.getAll<ExportLocation>(
                        `${this.exportLocationsEndpoint}?organisationId=${orgId}`
                    )
                ),
                catchError(error => {
                    console.error('ExportLocationsService:: Failed to retrieve export locations', error);
                    return of([]);
                })
            )
        );
    }

    get downloadableLocations$(): Observable<ExportLocation[]> {
        return this.getExportLocations().pipe(
            map((exportLocations: ExportLocation[]) =>
                exportLocations.filter((exportLocation: ExportLocation) => exportLocation.HttpAccessible)
            )
        );
    }

    get externalLocations$(): Observable<ExportLocation[]> {
        return this.getExportLocations().pipe(
            map((exportLocations: ExportLocation[]) =>
                exportLocations.filter((exportLocation: ExportLocation) => !exportLocation.HttpAccessible)
            )
        );
    }

    public getExportLocation(exportLocationId: string): Observable<ExportLocation> {
        const request = `${this.exportLocationsEndpoint}/${exportLocationId}`;

        return this.http.get<ExportLocation>(request);
    }

    public getExportLocationUsers(exportLocationId: string) {
        return this.http.get<Paginated<User>>(`${this.exportLocationsEndpoint}/${exportLocationId}/users`);
    }

    public getExportLocations(): Observable<ExportLocation[]> {
        return this.exportLocations$.pipe(take(1));
    }

    public getExportLocationsByOrganisationId(organisationId: OrganisationId): Observable<ExportLocation[]> {
        const params: HttpParams = new HttpParams({ encoder: new CustomHttpParamEncoder() }).set(
            'organisationId',
            organisationId
        );

        return this.paginationService.getAll(this.exportLocationsEndpoint, params);
    }

    private initDefaultExportLocation(): Observable<ExportLocation> {
        const request = `${this.exportLocationsEndpoint}/default`;

        const defaultCall$: Observable<ExportLocation> = this.http.get<ExportLocation>(request).pipe(
            catchError((error: any) => {
                console.error(
                    'ExportLocationsService: no default export location found. Attempting to return the first export location possible.'
                );
                try {
                    return this.getExportLocations().pipe(
                        map(
                            (allExportLocations: ExportLocation[]) =>
                                allExportLocations.filter(exportLocation => exportLocation.HttpAccessible)[0]
                        )
                    );
                } catch {
                    console.error(
                        'ExportLocationsService: no export locations found. None at all. Zilch. Zero. Nothing.'
                    );
                    return throwError(error);
                }
            })
        );

        const cachedDefaultExportLocation$: Observable<any> = this.apiCachingHelper.cachedApiCall(defaultCall$);
        cachedDefaultExportLocation$.subscribe(
            (defaultExportLocation: ExportLocation) => (this.defaultExportLocation$ = of(defaultExportLocation))
        );

        return cachedDefaultExportLocation$.pipe(take(1));
    }

    public getDefaultExportLocation(): Observable<ExportLocation> {
        return this.defaultExportLocation$ ? this.defaultExportLocation$ : this.initDefaultExportLocation();
    }

    public createExportLocation(
        exportLocation: Partial<ExportLocation>,
        updateCredentials: boolean
    ): Observable<ExportLocation> {
        const params: HttpParams = new HttpParams().set('saveCredentials', updateCredentials);

        return this.http.post(this.exportLocationsEndpoint, exportLocation, undefined, params);
    }

    public updateExportLocation(
        exportLocation: Partial<ExportLocation>,
        updateCredentials: boolean
    ): Observable<ExportLocation> {
        const url: string = `${this.exportLocationsEndpoint}/${exportLocation.Id}`;
        const params: HttpParams = new HttpParams().set('saveCredentials', updateCredentials);

        return this.http.put(url, exportLocation, undefined, params);
    }

    public deleteExportLocation(exportLocationId: ExportLocationId): Observable<boolean> {
        const url: string = `${this.exportLocationsEndpoint}/${exportLocationId}`;
        return this.http.delete(url).pipe(map(() => true));
    }

    public updateExportLocationUsers(exportLocationId: ExportLocationId, users: UserId[]): Observable<void> {
        const url: string = `${this.exportLocationsEndpoint}/${exportLocationId}/users`;
        return this.http.put(url, users);
    }
}
